import { TranslatableError } from '../../../translation/translatable-error.js';
import * as filterFactory from '../factory.js';
import { simplifyFilterConfig } from '../filter-config-utils.js';
import { createMeasureFromRankingFilterJaql } from './attribute-measure-util.js';
import { ConditionFilterType } from './types.js';
const isTopCondition = (filter) => filter.top !== undefined;
const isBottomCondition = (filter) => filter.bottom !== undefined;
const isExcludeCondition = (filter) => { var _a; return !!((_a = filter.exclude) === null || _a === void 0 ? void 0 : _a.members); };
const isMembersCondition = (filter) => !!filter.members && !!filter.isCondition;
const isWithinCondition = (filter) => { var _a, _b; return ((_a = filter.last) === null || _a === void 0 ? void 0 : _a.anchor) !== undefined || ((_b = filter.next) === null || _b === void 0 ? void 0 : _b.anchor) !== undefined; };
const isGreaterThanCondition = (filter) => filter.fromNotEqual !== undefined;
const isGreaterThanOrEqualCondition = (filter) => filter.from !== undefined && !filter.isBetween;
const isLessThanCondition = (filter) => filter.toNotEqual !== undefined;
const isLessThanOrEqualCondition = (filter) => filter.to !== undefined && !filter.isBetween;
const isEqualsCondition = (filter) => filter.equals !== undefined;
const isNotEqualCondition = (filter) => filter.doesntEqual !== undefined;
const isEmptyCondition = (filter) => !!(filter.equals === '' && filter.isEmpty);
const isNotEmptyCondition = (filter) => !!(filter.doesntEqual === '' && filter.isEmpty);
const isContainsCondition = (filter) => filter.contains !== undefined;
const isDoesntContainCondition = (filter) => filter.doesntContain !== undefined;
const isStartsWithCondition = (filter) => filter.startsWith !== undefined;
const isDoesntStartsWithCondition = (filter) => filter.doesntStartWith !== undefined;
const isEndsWithCondition = (filter) => filter.endsWith !== undefined;
const isDoesntEndWithCondition = (filter) => filter.doesntEndWith !== undefined;
const isBetweenCondition = (filter) => filter.from !== undefined && filter.to !== undefined;
const isNotBetweenCondition = (filter) => { var _a, _b; return ((_a = filter.exclude) === null || _a === void 0 ? void 0 : _a.from) !== undefined && ((_b = filter.exclude) === null || _b === void 0 ? void 0 : _b.to) !== undefined; };
const isMultipleCondition = (filter) => !!(filter.or || filter.and);
export const getSelectedConditionOption = (filter) => {
    if (isBottomCondition(filter))
        return ConditionFilterType.BOTTOM;
    if (isTopCondition(filter))
        return ConditionFilterType.TOP;
    if (isExcludeCondition(filter))
        return ConditionFilterType.IS_NOT;
    if (isWithinCondition(filter))
        return ConditionFilterType.IS_WITHIN;
    if (isGreaterThanCondition(filter))
        return ConditionFilterType.GREATER_THAN;
    if (isLessThanCondition(filter))
        return ConditionFilterType.LESS_THAN;
    if (isEqualsCondition(filter))
        return ConditionFilterType.EQUALS;
    if (isNotEqualCondition(filter))
        return ConditionFilterType.DOESNT_EQUAL;
    if (isEmptyCondition(filter))
        return ConditionFilterType.IS_EMPTY;
    if (isNotEmptyCondition(filter))
        return ConditionFilterType.IS_NOT_EMPTY;
    if (isContainsCondition(filter))
        return ConditionFilterType.CONTAINS;
    if (isDoesntContainCondition(filter))
        return ConditionFilterType.DOESNT_CONTAIN;
    if (isDoesntEndWithCondition(filter))
        return ConditionFilterType.DOESNT_END_WITH;
    if (isDoesntStartsWithCondition(filter))
        return ConditionFilterType.DOESNT_START_WITH;
    if (isEndsWithCondition(filter))
        return ConditionFilterType.ENDS_WITH;
    if (isStartsWithCondition(filter))
        return ConditionFilterType.STARTS_WITH;
    if (isNotBetweenCondition(filter))
        return ConditionFilterType.IS_NOT_BETWEEN;
    if (isMembersCondition(filter))
        return ConditionFilterType.IS;
    if (isMultipleCondition(filter))
        return ConditionFilterType.MULTIPLE_CONDITION;
    // Need to verify BETWEEN case before the GREATER_THAN_OR_EQUAL and LESS_THAN_OR_EQUAL due to missing `filter.isBetween` property in some cases
    if (isBetweenCondition(filter))
        return ConditionFilterType.BETWEEN;
    if (isGreaterThanOrEqualCondition(filter))
        return ConditionFilterType.GREATER_THAN_OR_EQUAL;
    if (isLessThanOrEqualCondition(filter))
        return ConditionFilterType.LESS_THAN_OR_EQUAL;
    return ConditionFilterType.NONE;
};
/**
 * Creates an attribute filter from the provided attribute and condition filter JAQL object
 *
 * @param attribute - Provided attribute
 * @param conditionFilterJaql - Condition filter JAQL object
 * @param guid - GUID for the filter
 * @returns attribute filter
 */
export const createAttributeFilterFromConditionFilterJaql = (attribute, conditionFilterJaql, guid) => {
    var _a, _b, _c, _d, _e, _f, _g;
    const conditionType = getSelectedConditionOption(conditionFilterJaql);
    switch (conditionType) {
        case ConditionFilterType.BOTTOM:
            if (conditionFilterJaql.by) {
                return filterFactory.bottomRanking(attribute, createMeasureFromRankingFilterJaql(conditionFilterJaql.by, conditionFilterJaql.rankingMessage), conditionFilterJaql[ConditionFilterType.BOTTOM], { guid });
            }
            break;
        case ConditionFilterType.EQUALS:
            return filterFactory.equals(attribute, conditionFilterJaql[ConditionFilterType.EQUALS], { guid });
        case ConditionFilterType.DOESNT_EQUAL:
            return filterFactory.doesntEqual(attribute, conditionFilterJaql[ConditionFilterType.DOESNT_EQUAL], { guid });
        case ConditionFilterType.GREATER_THAN:
            return filterFactory.greaterThan(attribute, conditionFilterJaql[ConditionFilterType.GREATER_THAN], { guid });
        case ConditionFilterType.GREATER_THAN_OR_EQUAL:
            return filterFactory.greaterThanOrEqual(attribute, conditionFilterJaql[ConditionFilterType.GREATER_THAN_OR_EQUAL], { guid });
        case ConditionFilterType.TOP:
            if (conditionFilterJaql.by) {
                return filterFactory.topRanking(attribute, createMeasureFromRankingFilterJaql(conditionFilterJaql.by, conditionFilterJaql.rankingMessage), conditionFilterJaql[ConditionFilterType.TOP], { guid });
            }
            break;
        case ConditionFilterType.STARTS_WITH:
            return filterFactory.startsWith(attribute, conditionFilterJaql[ConditionFilterType.STARTS_WITH], { guid });
        case ConditionFilterType.DOESNT_START_WITH:
            return filterFactory.doesntStartWith(attribute, conditionFilterJaql[ConditionFilterType.DOESNT_START_WITH], { guid });
        case ConditionFilterType.ENDS_WITH:
            return filterFactory.endsWith(attribute, conditionFilterJaql[ConditionFilterType.ENDS_WITH], { guid });
        case ConditionFilterType.DOESNT_END_WITH:
            return filterFactory.doesntEndWith(attribute, conditionFilterJaql[ConditionFilterType.DOESNT_END_WITH], { guid });
        case ConditionFilterType.CONTAINS:
            return filterFactory.contains(attribute, conditionFilterJaql[ConditionFilterType.CONTAINS], { guid });
        case ConditionFilterType.DOESNT_CONTAIN:
            return filterFactory.doesntContain(attribute, conditionFilterJaql[ConditionFilterType.DOESNT_CONTAIN], { guid });
        case ConditionFilterType.LESS_THAN:
            return filterFactory.lessThan(attribute, conditionFilterJaql[ConditionFilterType.LESS_THAN], { guid });
        case ConditionFilterType.LESS_THAN_OR_EQUAL:
            return filterFactory.lessThanOrEqual(attribute, conditionFilterJaql[ConditionFilterType.LESS_THAN_OR_EQUAL], { guid });
        case ConditionFilterType.BETWEEN:
            return filterFactory.between(attribute, conditionFilterJaql.from, conditionFilterJaql.to, { guid });
        case ConditionFilterType.IS_NOT_BETWEEN:
            return filterFactory.exclude(filterFactory.between(attribute, (_a = conditionFilterJaql.exclude) === null || _a === void 0 ? void 0 : _a.from, (_b = conditionFilterJaql.exclude) === null || _b === void 0 ? void 0 : _b.to, { guid }), undefined, { guid });
        case ConditionFilterType.MULTIPLE_CONDITION:
            if (conditionFilterJaql.and) {
                return filterFactory.intersection(conditionFilterJaql.and.map((c) => createAttributeFilterFromConditionFilterJaql(attribute, c, guid)), { guid });
            }
            if (conditionFilterJaql.or) {
                return filterFactory.union(conditionFilterJaql.or.map((c) => createAttributeFilterFromConditionFilterJaql(attribute, c, guid)), { guid });
            }
            break;
        case ConditionFilterType.IS_NOT: {
            const deactivatedMembers = (((_c = conditionFilterJaql.filter) === null || _c === void 0 ? void 0 : _c.turnedOff) && ((_d = conditionFilterJaql.filter.exclude) === null || _d === void 0 ? void 0 : _d.members)) ||
                [];
            const selectedMembers = ((_f = (_e = conditionFilterJaql.exclude) === null || _e === void 0 ? void 0 : _e.members) === null || _f === void 0 ? void 0 : _f.filter((member) => !deactivatedMembers.includes(member))) || [];
            // use members filter with exclude instead of exclude filter
            const config = simplifyFilterConfig({
                guid,
                excludeMembers: true,
                enableMultiSelection: (_g = conditionFilterJaql.multiSelection) !== null && _g !== void 0 ? _g : true,
                deactivatedMembers,
            });
            return filterFactory.members(attribute, selectedMembers, config);
        }
        case ConditionFilterType.AFTER:
        case ConditionFilterType.BEFORE:
        case ConditionFilterType.IS_EMPTY:
        case ConditionFilterType.IS_NOT_EMPTY:
            // TODO Handle these cases later; may need filterFactory function added first
            break;
    }
    throw new TranslatableError('errors.filter.unsupportedConditionFilter', {
        filter: JSON.stringify(conditionFilterJaql),
    });
};
/**
 * Creates a measure filter from the provided measure and condition filter JAQL object
 *
 * @param measure - Provided measure
 * @param conditionFilterJaql - Condition filter JAQL object
 * @param guid - GUID for the filter
 * @returns measure filter
 */
export const createMeasureFilterFromConditionFilterJaql = (measure, conditionFilterJaql, guid) => {
    var _a, _b;
    const conditionType = getSelectedConditionOption(conditionFilterJaql);
    switch (conditionType) {
        case ConditionFilterType.EQUALS:
            return filterFactory.measureEquals(measure, conditionFilterJaql[ConditionFilterType.EQUALS], { guid });
        case ConditionFilterType.GREATER_THAN:
            return filterFactory.measureGreaterThan(measure, conditionFilterJaql[ConditionFilterType.GREATER_THAN], { guid });
        case ConditionFilterType.GREATER_THAN_OR_EQUAL:
            return filterFactory.measureGreaterThanOrEqual(measure, conditionFilterJaql[ConditionFilterType.GREATER_THAN_OR_EQUAL], { guid });
        case ConditionFilterType.LESS_THAN:
            return filterFactory.measureLessThan(measure, conditionFilterJaql[ConditionFilterType.LESS_THAN], { guid });
        case ConditionFilterType.LESS_THAN_OR_EQUAL:
            return filterFactory.measureLessThanOrEqual(measure, conditionFilterJaql[ConditionFilterType.LESS_THAN_OR_EQUAL], { guid });
        case ConditionFilterType.BETWEEN:
            return filterFactory.measureBetween(measure, conditionFilterJaql.from, conditionFilterJaql.to, { guid });
        case ConditionFilterType.IS_NOT_BETWEEN:
            return filterFactory.exclude(filterFactory.measureBetween(measure, (_a = conditionFilterJaql.exclude) === null || _a === void 0 ? void 0 : _a.from, (_b = conditionFilterJaql.exclude) === null || _b === void 0 ? void 0 : _b.to, { guid }), undefined, { guid });
    }
    throw new TranslatableError('errors.filter.unsupportedConditionFilter', {
        filter: JSON.stringify(conditionFilterJaql),
    });
};
//# sourceMappingURL=condition-filter-util.js.map